import { Injectable } from '@angular/core';

export interface CalendarQuickNote {
  id: string;
  dateKey: string;
  text: string;
  createdAt: string;
  conversationId?: string;
  conversationTitle?: string;
}

@Injectable({ providedIn: 'root' })
export class CalendarNotesService {
  private readonly storageKey = 'clarichat_calendar_quick_notes';
  private notes: CalendarQuickNote[] = [];

  constructor() {
    this.notes = this.loadFromStorage();
  }

  getNotesForDate(dateKey: string): CalendarQuickNote[] {
    return this.notes
      .filter(note => note.dateKey === dateKey)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getNotesCountMap(): Map<string, number> {
    const map = new Map<string, number>();
    for (const note of this.notes) {
      map.set(note.dateKey, (map.get(note.dateKey) || 0) + 1);
    }
    return map;
  }

  addNote(note: Omit<CalendarQuickNote, 'id' | 'createdAt'>): CalendarQuickNote {
    const createdNote: CalendarQuickNote = {
      ...note,
      id: `note-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    this.notes = [createdNote, ...this.notes];
    this.persist();
    return createdNote;
  }

  deleteNote(noteId: string): void {
    this.notes = this.notes.filter(note => note.id !== noteId);
    this.persist();
  }

  private loadFromStorage(): CalendarQuickNote[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as CalendarQuickNote[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    localStorage.setItem(this.storageKey, JSON.stringify(this.notes));
  }
}
